import OfferInclusionsList from 'components/Common/Inclusions/OfferInclusionsList'
import LineCalendarIcon from 'LuxKit/Icons/line/LineCalendarIcon'
import Modal from 'LuxKit/Modal/Modal'
import BedbankCancellationPolicy from 'components/OfferPage/Bedbank/BedbankCancellationPolicy'
import Group from 'components/utils/Group'
import ModalContext from 'contexts/ModalContext'
import useOfferMetaData from 'hooks/Offers/useOfferMetaData'
import useBedbankOfferSearchPricing from 'hooks/useBedbankOfferSearchPricing'
import { EmptyObject } from 'lib/object/objectUtils'
import { getHotelOfferUrl } from 'lib/offer/offerPageURL'
import { buildBedbankCancellationPolicy } from 'lib/search/hotels'
import { useCallback, useContext, useMemo } from 'react'
import { useIntl } from 'react-intl'
import ExperimentalSearchTileFeaturesList, { MAX_INCLUSIONS_TO_SHOW } from '../shared/ExperimentalSearchTileFeaturesList'
import LoyaltyInclusions from 'loyalty/components/LoyaltyInclusions'
import useLoyaltyBenefitInclusions from 'loyalty/hooks/useLoyaltyBenefitInclusions'

interface Props {
  offer: App.BedbankOffer | App.BedbankOfferSummary
  filters?: App.OfferListFilters
}

function ExperimentalBedbankLTSSearchTileFeatures({ offer, filters = EmptyObject }: Props) {
  const intl = useIntl()
  const { shownRate } = useBedbankOfferSearchPricing(offer, filters)
  const offerMetaData = useOfferMetaData(offer.id, filters)
  const cancellationPolicy = useMemo(() => {
    return buildBedbankCancellationPolicy(
      offerMetaData?.cancellationPolicyInfo,
      filters,
      offer.property.timezone,
    )
  }, [offerMetaData?.cancellationPolicyInfo, filters, offer])
  const showModal = useContext(ModalContext)
  const loyaltyCount = useLoyaltyBenefitInclusions(offer).length
  const showLoyaltyInline = ((offer.inclusions?.length ?? 0) + loyaltyCount) <= MAX_INCLUSIONS_TO_SHOW

  const openAllInclusionsModal = useCallback(() => {
    showModal(<Modal
      title={offer.property.name}
      primaryActionTo={getHotelOfferUrl(offer.id, offer.type)}
      primaryActionText={intl.formatMessage({
        id: 'o5cLrPpVrrMf845Pglrx',
        defaultMessage: 'View offer',
        description: 'CTA button in experimental bedbank LTS inclusions modal to view the offer',
      })}
    >
      <Group direction="vertical" gap={4}>
        {!!offer.inclusions?.length && <OfferInclusionsList inclusions={offer.inclusions} />}
        <LoyaltyInclusions offer={offer} variant="offer-page" />
      </Group>
    </Modal>)
  }, [showModal, offer, intl])

  if (!offer.inclusions?.length && !loyaltyCount) return null

  return (
    <Group direction="vertical" gap={4}>
      {!!offer.inclusions?.length && <ExperimentalSearchTileFeaturesList
        features={offer.inclusions}
        featuresType="inclusions"
        cancellationPolicy={(offer.productType === 'limited_time_special' && (shownRate || cancellationPolicy)) && <BedbankCancellationPolicy
          icon={<LineCalendarIcon />}
          rate={shownRate ?? cancellationPolicy}
          showNonCancellation
        />}
        onMoreClick={openAllInclusionsModal}
        additionalHiddenCount={showLoyaltyInline ? 0 : loyaltyCount}
      />}
      {showLoyaltyInline && <LoyaltyInclusions offer={offer} variant="search-tile" />}
    </Group>
  )
}

export default ExperimentalBedbankLTSSearchTileFeatures
